import type { ILaunchpad } from '@tacans/shared-marketplace-layer';
import { LotAPI, NftAPI, WalletProviderE } from '@tacans/shared-marketplace-layer';
import { useTranslation } from 'next-i18next';
import Router from 'next/router';
import React, { useCallback } from 'react';
import { walletCoins } from 'src/constants/common.constants';
import { useAuthorized } from 'src/features/common/auth/hooks/auth.hooks';
import { MintNowModal } from 'src/features/common/collectible/modals/mint-now-modal';
import { useLaunchpadStatus } from 'src/features/common/launchpad/hooks/use-launchpad-status';
import { UpdateLaunchpadStatusModal } from 'src/features/common/launchpad/modals/update-launchpad-status';
import { useProfile } from 'src/features/common/profile/hooks/profile.hooks';
import { useUserListEntities } from 'src/features/common/user/hooks/user-list.hooks';
import { useIsAlreadyConnectedWallet } from 'src/features/common/wallet/hooks/use-is-already-connected-wallet';
import { useBlockChainsById } from 'src/features/common/wallet/hooks/wallet.hooks';
import { ConnectWalletModal } from 'src/features/common/wallet/modals/connect-wallet-modal';
import { useGetUniverseTemplate } from 'src/hooks/use-get-universe-template';
import { useNotifications } from 'src/services/notifications/use-notifications';
import { Button } from 'src/ui/common/atoms/button';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { Grid } from 'src/ui/common/atoms/grid';
import { makePriceByNano } from 'src/utils/entity.utils';
import { StyledDDK, StyledItem, StyledItemLabel, StyledItemValue } from './collection-statistics.styles';

interface IProps {
    className?: string;
    launchpad: ILaunchpad;
    disabled: boolean;
}

export const LaunchpadStatistics: React.VFC<IProps> = ({ disabled, className, launchpad }) => {
    const { t } = useTranslation('components');
    const { t: tPage } = useTranslation('pages');
    const blockchains = useBlockChainsById();
    const profile = useProfile();
    const { error } = useNotifications();
    const isAuthorized = useAuthorized();
    const walletProvider = blockchains[launchpad.blockchainId]?.name as WalletProviderE;
    const isAlreadyConnected = useIsAlreadyConnectedWallet(walletProvider);
    const coinName = walletCoins[walletProvider];
    const userEntities = useUserListEntities();
    const owner = launchpad && userEntities[launchpad.ownerId];
    const isSuperlabOwner = process.env.NEXT_PUBLIC_NFT_SUPERLAB_OWNER_ID === owner?.id;
    const { isSyndicate } = useGetUniverseTemplate();

    const handleMintNow = useCallback(async () => {
        // fetch random nft to mint
        const nftsCount = +(launchpad.totalItems || 0) - +(launchpad.mintedItems || 0);
        if (nftsCount === 0) {
            return;
        }
        if (!isAuthorized) {
            Router.push('/login');
            return;
        }
        if (!isAlreadyConnected) {
            ConnectWalletModal.open({ walletProvider });
            return;
        }
        const res = await NftAPI.fetchByLaunchpad({
            launchpadId: launchpad.id,
            pager: {
                page: Math.floor(Math.random() * nftsCount),
                limit: 1,
            },
        });
        if (!res.data.list) {
            error({
                text: 'No NFTs to buy',
            });
            return;
        }
        const nft = {
            ...res.data.list[0],
            previewAssets: launchpad.blurTokens ? launchpad.avatarAssets : res.data.list[0].previewAssets,
            royalties: launchpad.royalties,
        };
        const lotsRes = await LotAPI.fetchLotsByNft({
            nftId: nft.id,
            pager: { page: 0, limit: 1 },
        });

        const addressResponse = await NftAPI.fetchScAddress(lotsRes.data.lots[0].nftId);
        const { address = '' } = addressResponse.data;
        const scInfoPayload = address ? { address } : { standard: nft.standard.toUpperCase() };
        MintNowModal.open({
            blur: !!launchpad.blurTokens,
            nft,
            lot: {
                ...lotsRes.data.lots[0],
                nanoPrice: launchpad.nanoPrice,
            },
            scInfoPayload,
        });
    }, [isAuthorized, error, isAlreadyConnected, launchpad, walletProvider]);

    const handleStartStop = useCallback(() => {
        if (!launchpad.isEnabled && !launchpad.totalItems) {
            error({
                text: t('launchpad.notification.notRunning.title'),
                description: t('launchpad.notification.notRunning.description'),
            });
            return;
        }
        UpdateLaunchpadStatusModal.open({
            launchpad,
        });
    }, [error, launchpad, t]);

    const isOwner = profile?.user?.id === launchpad.ownerId;
    const price = launchpad?.nanoPrice ? makePriceByNano(+launchpad.nanoPrice) : '0';
    const status = useLaunchpadStatus({ launchpad });

    return (
        <Grid className={className} xs={3} columnGap={3} rowGap={3}>
            <StyledItem>
                <StyledItemValue medium>{launchpad?.totalItems ?? 0}</StyledItemValue>
                <StyledItemLabel size="xxs" sizeMd="base">
                    {t('statistics.totalItems')}
                </StyledItemLabel>
            </StyledItem>
            <StyledItem>
                <StyledItemValue medium>
                    {price} {coinName}
                </StyledItemValue>
                {isSuperlabOwner && <StyledDDK>≈ 2,000 DKK</StyledDDK>}
                <StyledItemLabel size="xxs" sizeMd="base">
                    {t('statistics.mintPrice')}
                </StyledItemLabel>
            </StyledItem>
            <StyledItem fixedColumnWidth={status.isPlanned}>
                <StyledItemValue medium color="white">
                    {status.label}
                </StyledItemValue>
                <StyledItemLabel size="xxs" sizeMd="base">
                    {t('statistics.mintDate')}
                </StyledItemLabel>
            </StyledItem>
            <StyledItem isSuperlabOwner={isSuperlabOwner}>
                <FlexBox alignItems="center" justifyContent="space-between">
                    {!isSuperlabOwner && (
                        <div>
                            <StyledItemValue medium>
                                {launchpad.mintedItems ?? 0} / {launchpad.totalItems ?? 0}
                            </StyledItemValue>
                            <StyledItemLabel size="xxs" sizeMd="base">
                                {t('statistics.mintedItems')}
                            </StyledItemLabel>
                        </div>
                    )}

                    {isOwner && (
                        <Button disabled={disabled} variant="white" onClick={handleStartStop} stretch={isSuperlabOwner}>
                            {launchpad.isEnabled ? tPage('launchpad.controls.stop') : tPage('launchpad.controls.start')}
                        </Button>
                    )}
                    {!isOwner && launchpad.isEnabled && (
                        <Button
                            disabled={disabled}
                            onClick={handleMintNow}
                            stretch={isSuperlabOwner}
                            variant={isSyndicate ? 'white' : 'gradient'}
                        >
                            {tPage('launchpad.controls.mintNow')}
                        </Button>
                    )}
                </FlexBox>
            </StyledItem>
        </Grid>
    );
};